import {
	PositionDirection,
	BigNum,
	QUOTE_PRECISION_EXP,
	QUOTE_PRECISION,
	BASE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	PRICE_PRECISION,
} from '@velocity-exchange/sdk';
import { PublicKey } from '@solana/web3.js';
import {
	matchEnum,
	UIMarket,
	UISerializablePositionHistoryRecord,
	calculateAverageEntryPrice,
} from '@velocity-exchange/common';
import { PositionHistoryRowData } from './types';

export function transformRecord(
	record: UISerializablePositionHistoryRecord,
	currentAccountPubkey: PublicKey | undefined
): PositionHistoryRowData {
	const uiMarket = new UIMarket(record.marketIndex, record.marketType);
	const marketName = uiMarket.uiSymbols.marketDisplaySymbol;
	const isTaker =
		currentAccountPubkey && record.taker?.equals(currentAccountPubkey);

	const quoteEntryAmountForPnl = record.userExistingQuoteEntryAmount;

	const avgExitPrice = calculateAverageEntryPrice(
		record.quoteAssetAmountFilled ?? BigNum.zero(QUOTE_PRECISION_EXP),
		record.baseAssetAmountFilled ?? BigNum.zero(BASE_PRECISION_EXP)
	);

	const orderDirection = isTaker
		? record.takerOrderDirection
		: record.makerOrderDirection;

	const isOrderLong = matchEnum(orderDirection, PositionDirection.LONG);
	const isOrderShort = matchEnum(orderDirection, PositionDirection.SHORT);
	const direction = isOrderLong ? ('short' as const) : ('long' as const);

	const entryPrice = (() => {
		try {
			const baseAmount = record.baseClosedForPnl?.toNum() ?? 0;
			if (baseAmount === 0) return BigNum.zero(PRICE_PRECISION_EXP);

			return BigNum.from(
				((quoteEntryAmountForPnl?.toNum() ?? 0) / baseAmount) *
					PRICE_PRECISION.toNumber(),
				PRICE_PRECISION_EXP.toNumber()
			);
		} catch {
			return BigNum.zero(PRICE_PRECISION_EXP);
		}
	})();

	const pnl = (() => {
		try {
			return (
				(isOrderShort
					? avgExitPrice.sub(entryPrice).toNum() *
						(record.baseClosedForPnl?.toNum() ?? 0)
					: entryPrice.sub(avgExitPrice).toNum() *
						(record.baseClosedForPnl?.toNum() ?? 0)) -
				(record.userFee?.toNum() ?? 0)
			);
		} catch {
			return 0;
		}
	})();

	const pnlScaledUp = pnl * QUOTE_PRECISION.toNumber();

	const pnlNotional = (() => {
		try {
			return BigNum.from(pnlScaledUp, QUOTE_PRECISION_EXP).toNotional();
		} catch {
			return 'ERROR';
		}
	})();

	const positionPnlNotional = (() => {
		try {
			const positionPnlComponent = isOrderShort
				? avgExitPrice.sub(entryPrice).toNum() *
					(record.baseClosedForPnl?.toNum() ?? 0)
				: entryPrice.sub(avgExitPrice).toNum() *
					(record.baseClosedForPnl?.toNum() ?? 0);

			const positionPnlScaled =
				positionPnlComponent * QUOTE_PRECISION.toNumber();
			return BigNum.from(positionPnlScaled, QUOTE_PRECISION_EXP).toNotional();
		} catch {
			return '-';
		}
	})();

	const feesAndFundingNotional = (() => {
		try {
			const feesAndFundingNum = -(record.userFee?.toNum() ?? 0);
			const feesAndFundingScaled =
				feesAndFundingNum * QUOTE_PRECISION.toNumber();
			return BigNum.from(
				feesAndFundingScaled,
				QUOTE_PRECISION_EXP
			).toNotional();
		} catch {
			return '-';
		}
	})();

	const pricePercentChangeDisplay = (() => {
		try {
			const entryNum = entryPrice.toNum();
			const avgExitNum = avgExitPrice.toNum();
			if (entryNum <= 0 || avgExitNum <= 0) return '0.00%';
			const percentNum = ((avgExitNum - entryNum) / entryNum) * 100;
			return `${percentNum.toFixed(2)}%`;
		} catch {
			return '0.00%';
		}
	})();

	const baseClosedForPnlDisplay = `${record.baseClosedForPnl?.prettyPrint()} ${uiMarket.uiSymbols.baseAssetDisplaySymbol}`;

	return {
		id: `${record.takerOrderId}-${record.makerOrderId}-${
			isTaker ? 'taker' : 'maker'
		}`,
		uiMarket,
		marketName,
		direction,
		pnl,
		pnlNotional,
		positionPnlNotional,
		feesAndFundingNotional,
		entryPrice,
		avgExitPrice,
		pricePercentChangeDisplay,
		baseAssetAmountFilledIsZero:
			record.baseAssetAmountFilled?.eqZero() ?? false,
		baseClosedForPnlDisplay,
		date: new Date(record.ts.toNumber() * 1000),
		txSig: record.txSig,
	};
}
